import axios from 'utils/axios';

// Bounded by an explicit timeout: startCall's module-level `starting` flag
// stays true while this request is in flight, and a request that never
// settles would leave it wedged — from then on every dial is silently refused
// until a page refresh. With the timeout, axios rejects, startCall's catch
// resets `starting`, and the user gets an error card instead of a dead button.
const ACCESS_TOKEN_TIMEOUT_MS = 15000;

export async function fetchTwilioAccessToken() {
    try {
        const response = await axios.post('/api/org-id/{orgId}/clinic-id/{clinicId}/twilio/access-token', undefined, {
            timeout: ACCESS_TOKEN_TIMEOUT_MS
        });
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            throw e;
        }
        throw new Error('unknown error');
    }
}

// `clinicId` is passed explicitly because the axios interceptor's {clinicId}
// substitution only fires on /clinic/... paths, and the poll outlives that.
export async function fetchGeneratedCareSessionNote(clinicId, callSid) {
    return axios.get(`/api/org-id/{orgId}/clinic-id/${clinicId}/twilio/outbound-call/${callSid}/generated-note`);
}

// Server's view of a single outbound call: { status, errorCode, errorMessage,
// toNumber }. Polled briefly by the controller after a terminal SDK error to
// swap the browser's generic failure message for the real dial error the
// Twilio status callback persisted (the SDK only ever hears the 31000 text
// for server-rejected dials). 404 until the voice webhook inserts the row.
// `clinicId` is explicit for the same reason as the generated-note poll.
export async function fetchOutboundCallStatus(clinicId, callSid) {
    return axios.get(`/api/org-id/{orgId}/clinic-id/${clinicId}/twilio/outbound-call/${callSid}`);
}

// Mid-call consent denial: the server stops the Twilio recording and discards
// the partial recording/transcript, so no note is generated. Only valid while
// the call is in progress — the server responds 409 once the call has ended.
//
// Bounded by an explicit 15s timeout: the deny flow disables both consent
// buttons while denyRecordingConsentInFlight is true, so a request that never
// resolves would wedge them off indefinitely. On timeout axios rejects with
// code 'ECONNABORTED', which hits the controller's catch → denyRecordingConsentFailed
// (buttons re-enabled) + a "try again" snackbar, same as any other failure.
const DENY_RECORDING_CONSENT_TIMEOUT_MS = 15000;

export async function denyRecordingConsent(clinicId, callSid) {
    return axios.post(`/api/org-id/{orgId}/clinic-id/${clinicId}/twilio/outbound-call/${callSid}/deny-recording-consent`, undefined, {
        timeout: DENY_RECORDING_CONSENT_TIMEOUT_MS
    });
}

// Lightweight, role-agnostic read of a clinic's outbound-calling config. Unlike the
// clinic-settings endpoint, the global onPostAuth hook only enforces org/clinic ownership
// from the URL — any authenticated user with access to the clinic can call it.
// Response: { enabled: boolean, callerId: string | null }
export async function getOutboundCallingConfig(clinicId) {
    try {
        const response = await axios.get(`/api/org-id/{orgId}/clinic-id/${clinicId}/twilio/outbound-calling-config`);
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}
